// ─────────────────────────────────────────────────────────────────────────────
// MissionControl — the operator-facing surface for a domain that declares a root
// vocabulary. GROWTH is the first; a second is served by this same file.
//
// STRUCTURE IS THE APP; DATA IS THE API (owner ruling 2026-08-19).
//   • the nine root pills  ← SURFACE (surface.ts) — DECLARED, never gated on a read
//   • each root's sub-tabs ← SURFACE — likewise
//   • the rail's roster    ← the registry projection (/api/os/structure)
//   • every panel's data   ← GET /api/domain-board/<domain>
//
// A backend that has not restarted must not gate a single page. The pages render
// regardless; what is honestly empty is their DATA, never their existence.
//
// STILL: NO AGENT AND NO DOMAIN IS NAMED HERE. A controller reaches its root
// through its DECLARED ROLE (ROLE_ROOT), never through a name in this file, and
// the roster comes from the registry — so the agent registered next week appears
// with no edit. The one declared literal on the surface is SURFACE_BRAND, which
// is the surface's own title and is flagged as such where it is declared.
//
// GET ONLY. A RENDER IS NOT A MUTATION. The board's PUT twin is the sole writer
// server-side and this surface cannot reach it — the shared core does not export
// it. Opening any page writes nothing, and must never begin to.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useState } from 'react';
import { useDomainSurface, useActivity } from '../../data/dashboard';
import type { StructureAgent } from '../../data/dashboard';
import { MC, MC_FONT, MC_BODY_BG, MC_RAIL_BG, MC_PILL_ACTIVE, DOT } from './theme';
import { SURFACE, SURFACE_SUBTITLE, SURFACE_BRAND, RootSpec, SubTabSpec, ViewSpec, PanelSpec } from './surface';
import { RAIL_GROUPS, ROOT_RAIL, ROLE_ROOT, ROLE_RAIL, MINI_STATS, PANEL_ROOT, badgeOf, RailGroup, RailAccent } from './presentation';
import {
  Nav, INITIAL_NAV, rootForAgent,
  goRoot as navGoRoot, goSub as navGoSub, goView as navGoView,
  openAgent as navOpenAgent, openAttachedAdmin as navOpenAttachedAdmin,
} from './navigation';
import AgentRunControl, { RunDot } from '../agents/AgentRunControl';
// DELTA'S SURFACE PIECES — Pixel's, shipped, mounted here against the per-company
// board read (owner ruling 2026-09-14: mount what exists, build no second set).
import CompanySelector from '../delta/CompanySelector';
import CallGateThree from '../delta/CallGateThree';
import DeltaRunControl from '../delta/DeltaRunControl';
import BookingsPanel from '../delta/BookingsPanel';
import ParkedPanel from '../delta/ParkedPanel';
import HarnessRunControl from '../agents/HarnessRunControl';
import { Selection, getSelection, subscribeSelection } from '../../lib/deltaSelection';
import {
  OverviewBanner, KpiRow, ActivityPulse, BudgetRollup, AgentRoster,
  CeoReportPreview, TelcoRollup, ApiChangeWatch, PulseTicker, Orb,
  DeclaredPanel, AwaitingPanels, AgentStateDot, RootNoteBanner, RootStateDot,
} from './panels';
// THE SAME RESOLVER THE ROSTER USES. Not a copy — the rail and the roster must
// never disagree about one agent, so they share the function and the paint.
import { ceoDotState, isHarnessCeo, rosterDotLabel, rootDataState } from './rosterState';
import type { RootDataState } from './rosterState';
import type { CeoHealth } from './rosterState';

// ── OSIRIS IS CODE-SPLIT, AND THAT IS NOT AN OPTIMISATION ───────────────────
// Cesium is ~1.26 MB gzipped. Imported statically it lands in the main chunk, so
// EVERY user of the whole app downloads a globe engine to open Personal or
// Trading — a cost paid by everyone for a page almost nobody is on. Lazily, it
// is fetched the first time this one root is opened and never before.
// Measured, not assumed: static import took main.js from 0.58 MB to 1.84 MB.
const OsirisApp = React.lazy(() => import('./osiris/OsirisApp'));

/** The Sales sub-tab the company list sits on. A sub-tab KEY is identity in
 *  surface.ts ("neither the key nor the label moves"), so it is safe to name. */
const DELTA_SELECTOR_SUB = 'companies_sector_workbook';
/** The Sales sub-tab the call gate sits on — the Calls tab, and only there. */
const DELTA_CALLS_SUB = 'calls_objections_pivots';
/** The Sales sub-tab the booked consultations and their decks sit on (2026-09-29). */
const DELTA_PITCH_SUB = 'consultation_pitch';

// ── WHICH SALES TAB READS WHICH LIST (owner ruling 2026-09-16) ─────────────
// A company appears on a tab the moment THAT TAB'S work exists for it, so each
// tab's switcher offers its own completed set. A sub-tab absent from this map
// gets no dropdown rather than somebody else's list.
const DELTA_TAB_LIST: Record<string, 'research' | 'calls' | 'pitch'> = {
  company_research_diagnosis: 'research',
  companies_sector_workbook: 'research',
  calls_objections_pivots: 'calls',
  consultation_pitch: 'pitch',
};

export interface MissionControlProps {
  domain: string;
  authToken?: string | null;
  /** The agent currently open elsewhere in the app, by registry id. */
  activeAgent?: string | null;
  /** Opening an agent's CHAT. Distinct from opening its PAGE — the rail does the
   *  latter itself, in-surface, because an agent's page IS a root of this app. */
  onAgentClick?: (agent: string) => void;
  /**
   * WHERE THE OPERATOR IS — owned ABOVE this component (App.tsx).
   *
   * This surface unmounts whenever the app switches to the chat screen, so any
   * position held in its own state is destroyed by a trip through chat and the
   * surface reopens at the domain landing. Holding it above the unmount boundary
   * is what makes DASHBOARD return to the exact agent, root, sub-tab and view the
   * operator left. Chat is a door out and back, not a reset.
   *
   * Optional so the surface still works standalone (and under a static render);
   * when unmanaged it falls back to its own state and the round trip is not
   * preserved, which is correct for a surface nobody is navigating away from.
   */
  nav?: Nav;
  onNavChange?: (nav: Nav) => void;
}

/** Pair consecutive half-width panels; a full-width panel stands alone. */
function groupBySpan(panels: PanelSpec[]): PanelSpec[][] {
  const out: PanelSpec[][] = [];
  for (const p of panels) {
    const last = out[out.length - 1];
    if (p.span === 'full' || !last || last.length === 2 || last[0].span === 'full') out.push([p]);
    else last.push(p);
  }
  return out;
}

const ACCENT: Record<RailAccent, string> = {
  teal: MC.teal, blue: MC.blue, violet: MC.violet, amber: MC.amber, green: MC.green,
};

/** The rail groups that list ROOTS. Every other group lists AGENTS — see the
 *  note at `railRoots`. Declared here rather than tested for inside the loop so
 *  a group added to RAIL_GROUPS has to declare which kind of row it holds. */
const ROOT_ROW_GROUPS: RailGroup[] = ['OVERVIEW', 'APPS'];

/** hh:mm:ss, ticking. The design's live pulse clock — a CLOCK, not a data claim. */
function useClock(): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(now.getHours())}:${p(now.getMinutes())}:${p(now.getSeconds())}`;
}

export default function MissionControl({
  domain, activeAgent, onAgentClick, nav: navProp, onNavChange,
}: MissionControlProps) {
  // ONE SELECTION, EVERY TAB FOLLOWS. The selection store is Pixel's
  // (`lib/deltaSelection`); the board read carries it as `?company=` so the
  // `sales` root every Sales tab reads is composed for THAT company.
  const [deltaSel, setDeltaSel] = useState<Selection>(getSelection());
  useEffect(() => subscribeSelection(setDeltaSel), []);
  const selectedCompany = deltaSel.phase === 'selected' ? deltaSel.companyId : null;
  const feed = useDomainSurface(domain, selectedCompany);
  const activity = useActivity();
  const clock = useClock();

  // NAVIGATION — one value, owned above this component when managed. The root
  // list is DECLARED, so a landing root always exists and the surface is never in
  // a state where nothing can be shown.
  //
  // COMPANY AGENTS IS PARAMETERISED PER TENANT — one system, seven tenants, not
  // seven builds. The tenant rides in the same value: it is who you are looking
  // at, and it survives the same round trip that the position does.
  const [ownNav, setOwnNav] = useState<Nav>(INITIAL_NAV);
  const nav = navProp || ownNav;
  const setNav = (next: Nav) => { if (onNavChange) onNavChange(next); else setOwnNav(next); };
  const { root: rootKey, sub: subKey, view: viewKey, tenant } = nav;

  /**
   * RETURNING FROM CHAT LANDS ON THAT AGENT'S OWN PAGE.
   *
   * The agent view's DASHBOARD control routes back to this surface with
   * activeAgent still set, so the surface must reopen where the user left —
   * ALPHA's CEO root, BUCK's Marketing root — never the Overview.
   *
   * An effect is right here and a derivation is not: this is an external
   * navigation intent arriving as a prop, and the operator must stay free to
   * click elsewhere afterwards without being dragged back. It fires only when the
   * agent identity CHANGES, so it steers once and then lets go.
   */
  // NO ARRIVAL EFFECT. There used to be one here that re-opened `activeAgent`'s
  // root whenever this surface mounted. With the position owned above the unmount
  // boundary that effect became actively harmful: returning from chat remounts
  // this component with the SAME activeAgent still set, so it would fire and reset
  // the operator to that agent's landing — undoing the very round trip it now has
  // to preserve. Whoever changes the agent sets the position at the same moment;
  // arriving somewhere does not re-decide where you are.

  const root: RootSpec = useMemo(
    () => SURFACE.find((r) => r.key === rootKey) || SURFACE[0], [rootKey]);

  // The active sub-tab, DERIVED during render so the first paint is already
  // right and a root switch never shows a frame with no sub-tab selected.
  const activeSub: SubTabSpec | null = useMemo(() => {
    if (root.subTabs.length === 0) return null;
    return root.subTabs.find((t) => t.key === subKey) || root.subTabs[0];
  }, [root, subKey]);

  /** The active view within a sub-tab that has them. Derived during render like
   *  the other two levels, so the first paint is already on a real view. */
  const activeView: ViewSpec | null = useMemo(() => {
    const views = activeSub?.views;
    if (!views || views.length === 0) return null;
    return views.find((v) => v.key === viewKey) || views[0];
  }, [activeSub, viewKey]);

  const goRoot = (key: string) => setNav(navGoRoot(nav, key));

  /** Open a receptionist's ATTACHED ADMIN AGENT — its own surface, its own three
   *  views, parameterised to the same tenant. Reached from the rail's ↳ slot. */
  const openAttachedAdmin = (a: StructureAgent) => setNav(navOpenAttachedAdmin(nav, a));

  // DOES THE ROOT ON SCREEN BELONG TO AN AGENT? Derived by asking each agent
  // which root its DECLARED role points at — the same function the rail uses, so
  // the two can never disagree. A receptionist owns Company Agents only while it
  // is the selected tenant; otherwise the root is showing someone else's page.
  const rootOwner: StructureAgent | null = useMemo(() => {
    for (const a of feed.roster) {
      if (rootForAgent(a) !== root.key) continue;
      if (a.role?.id === 'receptionist') { if (tenant === a.id) return a; continue; }
      return a;
    }
    return null;
  }, [feed.roster, root.key, tenant]);

  // IS ANY ROLE DECLARED AT ALL? When the projection carries none, the roster
  // group holds EVERY agent — which is the inverted degrade rule working as
  // designed, but the design's heading would then assert that all of them are
  // receptionists. The heading says what is true instead.
  const anyRoleDeclared = useMemo(
    () => feed.roster.some((a) => !!a.role?.id), [feed.roster]);

  /** Opening an agent's PAGE. A controller lands on its root; a receptionist
   *  lands on Company Agents parameterised to itself. An agent whose role
   *  declares no root keeps its chat affordance and gains no invented page. */
  const openAgent = (a: StructureAgent) => {
    const next = navOpenAgent(nav, a);
    // An agent whose role declares no root keeps its chat affordance and gains no
    // invented page.
    if (!next) { if (onAgentClick) onAgentClick(a.id); return; }
    setNav(next);
  };

  // ROOT ROWS vs AGENT ROWS. Some rail groups list a ROOT (a page that exists
  // whether or not anyone owns it); the others list an AGENT (who is here, and
  // each row opens their page). A group is one or the other, never both, so the
  // two kinds of row are never mixed under one header.
  //
  // THIS IS A WHITELIST, NOT A TEST FOR 'NOT AN AGENT GROUP'. Written as
  // `!== 'OVERVIEW'` it read as "the one root group", and any group added later
  // silently rendered nothing — a rail row declared in ROOT_RAIL and simply
  // absent from the rail, which looks identical to never having declared it.
  // Naming the root groups means a new group must say which kind it is.
  const railRoots = useMemo(() => {
    const by: Record<string, RootSpec[]> = {};
    for (const r of SURFACE) {
      const placement = ROOT_RAIL[r.key];
      if (!placement || !ROOT_ROW_GROUPS.includes(placement.group)) continue;
      (by[placement.group] ||= []).push(r);
    }
    return by;
  }, []);

  /** DOMAIN CONTROLLERS — agents whose DECLARED role points at a root of this
   *  surface. Sorted by the root's declaration order so the rail reads in the
   *  same order as the tabs above it, rather than alphabetically by accident. */
  const controllerRows = useMemo(() => {
    const order = new Map(SURFACE.map((r, i) => [r.key, i]));
    return feed.roster
      .filter((a) => {
        const id = a.role?.id;
        if (!id || id === 'receptionist') return false;
        const rk = ROLE_ROOT[id];
        return !!rk && order.has(rk);
      })
      .sort((a, b) => (order.get(ROLE_ROOT[a.role!.id!])! - order.get(ROLE_ROOT[b.role!.id!])!));
  }, [feed.roster]);

  // WHO APPEARS IN THE ROSTER GROUP — decided by DECLARATION, not a name list.
  // An agent whose declared role already has a controller row above is not shown
  // twice. EVERY OTHER AGENT APPEARS: no declared role, an unmapped role, or a
  // role whose root this surface does not declare. Nothing can fall through.
  const rosterRows = useMemo(() => {
    const shown = new Set(controllerRows.map((a) => a.id));
    return feed.roster.filter((a) => !shown.has(a.id));
  }, [feed.roster, controllerRows]);

  /** Cross-domain agents — SERVICE and CODE team. Owner ruling: they come back
   *  under their own rail group rather than being unreachable on a full surface. */
  const crossDomain = useMemo(
    () => feed.allAgents.filter((a) => a.crossDomain), [feed.allAgents]);

  // The design's two tiles are CALLS TODAY and VOICE SPEND — both board-backed
  // measurements, not counts of the app's own structure. Neither has a source
  // until the board's first write, so both read "—". The HTML's figures are
  // sample data and none of them is transcribed here.
  const miniValue = (key: string): string => {
    const ov = (feed.board['overview'] || {}) as Record<string, unknown>;
    const v = ov[key];
    return (typeof v === 'number' || typeof v === 'string') ? String(v) : '—';
  };

  // THE WRONG COMPANY'S ROOT IS NOT PAINTED. While a new selection's read is in
  // flight the held board still belongs to the previous one; its `sales` root
  // renders unread for that moment rather than under the new company's name.
  const boardRoot = (root.key === ROLE_ROOT.sales && feed.boardFor !== selectedCompany
    ? {} : (feed.board[root.key] || {})) as Record<string, unknown>;
  // WHO THE OPERATOR IS LOOKING AT, resolved once. Panels that write `{tenant}`
  // in a title, a standing line or a pill fill it from here; null renders the em
  // dash, so an unparameterised root never shows a borrowed name.
  const tenantLabel = tenant
    ? (feed.agentById(tenant)?.label || feed.agentById(tenant)?.name || tenant)
    : null;

  /**
   * ONE AGENT ROW, used by BOTH groups. The two used to be written separately,
   * which is how the controller rows came to show a job title where the name
   * belongs while the receptionist rows were right. One renderer, one answer:
   *
   *   NAME      the agent's own name, from the registry — never role.label
   *   line 1    role.label, the DECLARED job title
   *   line 2    the function line for that role id (absent → omitted, not faked)
   *   ↳         the attached admin, for a receptionist or any declared attachment
   */
  // ALPHA'S HEALTH, READ ONCE for the whole surface. It rides the board poll the
  // page already holds — the rail makes no request of its own, per row or
  // otherwise, exactly as the roster does not.
  const ceoHealth = ((feed.board[PANEL_ROOT.ceo] || {}) as any).ceo_health as
    CeoHealth | undefined;

  const agentRow = (a: StructureAgent) => {
    const roleId = a.role?.id;
    const skin = (roleId && ROLE_RAIL[roleId]) || undefined;
    const accent = ACCENT[skin?.accent || 'violet'];
    const target = rootForAgent(a);
    const on = (target !== null && target === root.key
                && (roleId !== 'receptionist' || tenant === a.id)) || activeAgent === a.id;
    const admin = feed.agentById(a.attachedToId);
    return (
      <div key={a.id}
        style={{ ...S.railCard,
          background: on ? MC.panel : 'rgba(12,23,40,0.55)',
          borderColor: on ? accent : MC.panelEdgeSoft,
          boxShadow: on ? `${MC.shadowSoft}, inset 0 1px 0 ${MC.topHighlight}` : 'none' }}>
        <button type="button" onClick={() => openAgent(a)} style={S.railMain}>
        <span style={{ ...S.badge, color: accent, borderColor: accent,
                       background: on ? `${accent}1f` : 'transparent' }}>
          {badgeOf(a.name)}
        </span>
        <span style={S.railBody}>
          {/* THE NAME. The agent's own, always. */}
          <span style={{ ...S.railName, color: on ? MC.text : MC.textMuted }}>{a.name}</span>
          {/* The DECLARED job title — a descriptor, never the title of the row. */}
          <span style={S.railDesc}>{a.role?.label || a.kind || 'role not declared'}</span>
          {/* The function line. Omitted rather than invented when unknown. */}
          {skin?.fn && <span style={S.railDesc}>{skin.fn}</span>}
        </span>
        </button>
        {/* THE ↳ SLOT IS THE WAY INTO THE ATTACHED ADMIN AGENT — a sibling button,
            never nested inside the row's own button: a button inside a button is
            invalid and unreachable by keyboard. It names the declared admin where
            one is declared; where none is, it still opens the attached Admin's
            surface for this tenant, because the agent and its three views exist
            whether or not a pairing has been declared. No pairing is invented. */}
        {(admin || roleId === 'receptionist') && (
          <button type="button" style={S.attachBtn}
            onClick={() => openAttachedAdmin(a)}
            title="Open the attached Admin Agent">
            <span style={S.attachArrow}>↳</span>
            <span style={S.attachName}>{admin ? (admin.label || admin.name) : 'attached admin'}</span>
          </button>
        )}
        {/* ══ THE STATUS DOT (owner ruling 2026-08-31) ═══════════════════════
            It was `a.implementationState === 'live' ? DOT.live : DOT.idle`, and
            EVERY DOT ON THE SURFACE WAS GREY — the registry projection writes
            `LIVE_BUILT`, so that comparison could never be true. It was also
            answering the wrong question: the owner's is not "was this agent
            built", it is "is he working".

            IT COSTS NO VERTICAL SPACE, which is why the dot is what the rail
            gets and the control is not. Ten rows are ONE read — the poll is
            refcounted — and an agent with no harness holds no poll and stays a
            hollow grey, which is honest rather than hidden. */}
        {/* ══ ALPHA READS THE HEALTH, NOT THE RUNNER (owner ruling 2026-09-01)
            On this rail Alpha was an EMPTY RING beside Buck's filled dot, and
            for a real reason: `RunDot` answers "is this agent walking" off the
            growth-runner store, and Alpha has no harness there — so it fell to
            the hollow grey that means "nothing to read". Which was true of the
            question being asked, and useless.

            SAME SOURCE AS THE ROSTER, NOT A SECOND MECHANISM. Both call
            `ceoDotState` with the SAME `ceo_health` block off the SAME board
            poll, and both paint through the SAME `AgentStateDot`. Two
            indicators disagreeing about one agent is worse than one being
            absent, so there is one resolver and one colour table behind them.

            BY DECLARED ROLE, never by name — `harness_topology` declares
            ALPHA → {id: 'domain_ceo'} and every consumer derives it.

            Buck and the other four are untouched: they keep `RunDot`, which is
            the reference implementation and answers their question correctly. */}
        {isHarnessCeo(a as any)
          ? <span title={rosterDotLabel(a as any, ceoHealth)} style={{ lineHeight: 0 }}>
              <AgentStateDot state={ceoDotState(ceoHealth)} size={7} testId="rail-ceo-dot" />
            </span>
          : <RunDot domain={domain} agent={String(a.name || '').toLowerCase()} />}
      </div>
    );
  };

  return (
    <div style={S.root}>
      {/* ══ brand bar ══ */}
      <header style={S.brandBar}>
        <div style={S.brandLeft}>
          <Orb size={42} />
          <div style={S.brandText}>
            <div style={S.brandName}>{SURFACE_BRAND.name}</div>
            <div style={S.brandSub}>
              {SURFACE_BRAND.context} <span style={S.dotSep}>·</span> {SURFACE_BRAND.version}
            </div>
          </div>
        </div>
        <div style={S.clockWrap}>
          <span data-testid="brand-dot"
                style={{ ...S.dot, background: DOT.live, boxShadow: `0 0 8px ${MC.green}` }} />
          <span style={S.clockLabel}>LIVE PULSE</span>
          <span style={S.clock}>{clock}</span>
        </div>
      </header>

      {/* ══ page title ══ */}
      <div style={S.titleBlock}>
        <h1 style={S.title}>Mission Control</h1>
        <div style={S.subtitle}>{SURFACE_SUBTITLE}</div>
      </div>

      {/* ══ the nine root pills — DECLARED, never gated on a read ══ */}
      <nav style={S.tabs}>
        {SURFACE.map((r) => {
          const on = r.key === root.key;
          return (
            <button key={r.key} type="button" onClick={() => goRoot(r.key)}
              style={{
                ...S.tab,
                color: on ? MC.bg : MC.textMuted,
                background: on ? MC_PILL_ACTIVE : MC.panel,
                borderColor: on ? 'transparent' : MC.line,
                boxShadow: on ? '0 8px 22px rgba(54,230,212,.32)' : 'none',
                fontWeight: on ? 600 : 500,
              }}>
              {r.label}
            </button>
          );
        })}
      </nav>

      <div style={S.body}>
        {/* ══ the rail ══ */}
        <aside style={S.rail}>
          <div style={S.railScroll}>
            {RAIL_GROUPS.map((group: RailGroup) => {
              const roots = railRoots[group] || [];
              const isRoster = group === 'RECEPTIONIST AGENTS';
              const isCross = group === 'CROSS-DOMAIN';
              const isCtrl = group === 'DOMAIN CONTROLLERS';
              if (isCtrl && controllerRows.length === 0) return null;
              if (roots.length === 0 && !isRoster && !isCross && !isCtrl) return null;
              if (isCross && crossDomain.length === 0) return null;
              return (
                <div key={group} style={S.railGroup}>
                  <div style={S.railHead}>
                    {isRoster && !anyRoleDeclared && feed.ready
                      ? 'ALL REGISTERED AGENTS'
                      : group}
                  </div>
                  {isRoster && !anyRoleDeclared && feed.ready && (
                    <div style={S.railPending}>
                      no agent declares a role in this read, so none can be sorted into
                      controllers and receptionists — every registered agent is shown
                      rather than guessed at
                    </div>
                  )}

                  {roots.map((r) => {
                    const on = r.key === root.key;
                    const p = ROOT_RAIL[r.key];
                    const accent = ACCENT[p.accent || 'blue'];
                    // ══ WHAT THIS DOT MEANS (owner ruling 2026-09-02 — B) ══
                    // It was `hasOwnProperty(feed.board, r.key)` — "does a key
                    // with this root's name exist". `overview` is not a board
                    // root and never will be: the Overview panels read `ceo`,
                    // `pricing_budgets`, `supporting_systems` and `integrations`
                    // through PANEL_ROOT. So its dot had been idle since it
                    // shipped, truthfully answering the wrong question.
                    //
                    // Now it means THE PANELS THIS ROOT READS HAVE DATA, and
                    // where a root reads from is derived from its own
                    // declaration — PANEL_ROOT is handed in, so a panel
                    // repointed there moves this dot in the same edit.
                    const rootState = rootDataState(r, feed.board, PANEL_ROOT, feed.ready);
                    return (
                      <button key={r.key} type="button" onClick={() => goRoot(r.key)}
                        style={{
                          ...S.railCard,
                          background: on ? MC.panel : 'rgba(12,23,40,0.55)',
                          borderColor: on ? accent : MC.panelEdgeSoft,
                          boxShadow: on ? `${MC.shadowSoft}, inset 0 1px 0 ${MC.topHighlight}` : 'none',
                        }}>
                        <span style={{ ...S.badge, color: accent, borderColor: accent,
                                       background: on ? `${accent}1f` : 'transparent' }}>
                          {badgeOf(p.label)}
                        </span>
                        <span style={S.railBody}>
                          <span style={{ ...S.railName, color: on ? MC.text : MC.textMuted }}>{p.label}</span>
                          {(p.desc || '').split('\n').filter(Boolean).map((line, i) => (
                            <span key={i} style={S.railDesc}>{line}</span>
                          ))}
                        </span>
                        {/* THREE VALUES, AND THE THIRD IS THE POINT. `unread`
                            is the board read not having landed, and it renders
                            as ABSENCE — a hollow ring, the same language the
                            agent dots use — never as health and never as the
                            dim "looked, found nothing". */}
                        <RootStateDot state={rootState} style={S.rootDotSlot} />
                      </button>
                    );
                  })}

                  {isCtrl && controllerRows.map(agentRow)}

                  {isRoster && (
                    <>
                      {rosterRows.length === 0 && feed.ready && (
                        <div style={S.railPending}>
                          {feed.roster.length === 0
                            ? 'no agents registered — the registry read returned none for this domain'
                            : 'every registered agent is shown above under its declared role'}
                        </div>
                      )}
                      {!feed.ready && <div style={S.railPending}>reading the registry…</div>}
                      {rosterRows.map(agentRow)}
                    </>
                  )}

                  {isCross && crossDomain.map((a) => (
                    <button key={a.id} type="button"
                      disabled={!onAgentClick}
                      onClick={() => onAgentClick && onAgentClick(a.id)}
                      style={{ ...S.railCard, cursor: onAgentClick ? 'pointer' : 'default',
                               background: 'rgba(12,23,40,0.55)',
                               borderColor: activeAgent === a.id ? MC.blue : MC.panelEdgeSoft }}>
                      <span style={{ ...S.badge, color: MC.blue, borderColor: MC.blue }}>
                        {badgeOf(a.label || a.name)}
                      </span>
                      <span style={S.railBody}>
                        <span style={{ ...S.railName, color: MC.textMuted }}>{a.label || a.name}</span>
                        {/* Cross-domain agents have no page on THIS surface — they
                            belong to another domain. The row opens their chat. */}
                        <span style={S.railDesc}>
                          {a.role?.label || a.domainKey || 'cross-domain'}
                        </span>
                      </span>
                      {/* THE SAME DOT, AND THE SAME FIX. An instance fix leaves
                          twins: this row carried the identical
                          `implementationState === 'live'` comparison against a
                          projection that writes `LIVE_BUILT`, so it was grey for
                          the same never-true reason.

                          KEYED ON `a.domainKey`, NOT ON `domain`. These agents
                          belong to ANOTHER domain — that is what puts them in
                          this group — so pairing them with the surface's own
                          domain would be the domain/agent-pair drift the
                          harness test exists to refuse. None of them has a
                          harness today, so every one is a hollow grey, and that
                          is the honest answer rather than a hidden one. */}
                      <RunDot
                        domain={String(a.domainKey || '')}
                        agent={String(a.name || '').toLowerCase()}
                      />
                    </button>
                  ))}
                </div>
              );
            })}
          </div>

          <div style={S.miniRow}>
            {MINI_STATS.map((m) => (
              <div key={m.key} style={S.miniTile}>
                <div style={S.miniValue}>{miniValue(m.key)}</div>
                <div style={S.miniLabel}>{m.label}</div>
              </div>
            ))}
          </div>
        </aside>

        {/* ══ the well ══ */}
        <div style={S.well}>
          {/* ══ AN AGENT'S OWN PAGE: DASHBOARD | CHAT ══════════════════════════
              Owner ruling (2026-08-19): clicking an agent lands on its DASHBOARD,
              with chat available above — the dashboard is the house, the chat is a
              door into it. Same two-way shell the rest of the OS uses; only the
              landing changed. Rendered with this surface's sampled palette rather
              than the shared AgentTabBar, which is styled for the JARVIS console. */}
          {rootOwner && (
            <div style={S.agentBar}>
              <span style={S.agentWho}>
                <span style={{ ...S.badge, color: MC.violet, borderColor: MC.violet,
                               background: 'rgba(168,151,255,0.12)' }}>
                  {badgeOf(rootOwner.label || rootOwner.name)}
                </span>
                <span style={S.agentName}>{rootOwner.label || rootOwner.name}</span>
                <span style={S.agentRole}>{rootOwner.role?.label || 'role not declared'}</span>
              </span>
              <span style={S.agentTabs}>
                {/* ══ THE RUN CONTROL, ON THE BAR THE OWNER IS ACTUALLY LOOKING AT ══
                    Owner ruling 2026-08-31, second round: "AgentTabBar SHOWS
                    BU · BUCK · Marketing ON THE LEFT AND DASHBOARD · CHAT ON THE
                    RIGHT. NOTHING BETWEEN THEM." That bar is THIS one, not the
                    shared `AgentTabBar` — the comment above says so outright,
                    and on a FULL_SURFACE domain the agent's dashboard IS this
                    surface, so AgentViewScreen is never mounted for it.
                    Mounting the shared bar alone put the control on a screen
                    Buck does not have.

                    AN ABSENT ELEMENT IS NOT AN UNREADABLE ONE. Nothing rendered
                    here, so nothing could report a fault — which is why the
                    control now refuses to render silently at all (see
                    AgentRunControl's partial-pair branch).

                    THE PAIR IS {domain, rootOwner.name}, matching the rail's dot
                    — which resolves correctly, and is how we knew the surface
                    could reach the agent and the tab bar could not. `id` is
                    `os:agent:growth-buck`; the backend keys on the NAME. */}
                <AgentRunControl
                  domain={domain}
                  agent={String(rootOwner.name || '').toLowerCase()}
                  strip={false}
                  testId="agentbar-run-control"
                />
                <span style={{ ...S.agentTab, color: MC.bg, background: MC.teal, fontWeight: 650 }}>
                  DASHBOARD
                </span>
                <button type="button" disabled={!onAgentClick}
                  onClick={() => onAgentClick && onAgentClick(rootOwner.id)}
                  style={{ ...S.agentTab, color: MC.textMuted, background: 'transparent',
                           border: `1px solid ${MC.panelEdge}`,
                           cursor: onAgentClick ? 'pointer' : 'default' }}>
                  CHAT
                </button>
              </span>
            </div>
          )}

          {/* THE LIVE LINE, ON ITS OWN ROW. The agent bar is a tight flex row and
              this is a sentence — round N of five complete, the count, the lane.
              It renders only where there is a harness to report on, so nine of
              the ten agents get nothing here rather than an empty box. */}
          {rootOwner && (
            // THE SHARED RUN CONTROL (owner ruling 2026-09-14) — RUN · STOP ·
            // PAUSE · RESUME · DETAILS, the same component Delta's root mounts.
            // Renders nothing for an agent with no work harness, as RunStrip did.
            <HarnessRunControl domain={domain} agent={String(rootOwner.name || '').toLowerCase()} />
          )}

          {/* the root's sub-tab strip */}
          {root.subTabs.length > 0 && (
            <nav style={S.subTabs}>
              {root.subTabs.map((t) => {
                const on = t.key === activeSub?.key;
                return (
                  <button key={t.key} type="button" onClick={() => setNav(navGoSub(nav, t.key))}
                    style={{
                      ...S.subTab,
                      color: on ? MC.teal : MC.textDim,
                      borderBottomColor: on ? MC.teal : 'transparent',
                      fontWeight: on ? 650 : 500,
                    }}>
                    {/* The tenant parameterises the LABEL of the admin sub-tab, the
                        way the design writes it — one system, seven tenants. */}
                    {t.key === 'admin_agent' && tenant
                      ? `${(feed.agentById(tenant)?.label || feed.agentById(tenant)?.name || '')}-A Admin Agent`
                      : t.label}
                  </button>
                );
              })}
            </nav>
          )}

          {/* ══ the attached Admin Agent's own view switcher — the third level ══
              Rendered as the agent's own tab strip, because it IS an agent: the
              HTML nests exactly this switcher inside the receptionist's sixth
              sub-tab, and the nesting is what makes it an agent rather than two
              panels on someone else's page. */}
          {activeSub?.views && (
            <nav style={S.viewTabs}>
              {activeSub.views.map((v) => {
                const on = v.key === activeView?.key;
                return (
                  <button key={v.key} type="button" onClick={() => setNav(navGoView(nav, v.key))}
                    style={{ ...S.viewTab,
                      color: on ? MC.bg : MC.textMuted,
                      background: on ? MC.violet : 'transparent',
                      borderColor: on ? MC.violet : MC.line2,
                      fontWeight: on ? 650 : 500 }}>
                    {v.label}
                  </button>
                );
              })}
            </nav>
          )}

          {/* the tenant context line, when a root is parameterised */}
          {root.key === 'company_agents' && (
            <div style={S.tenantBar}>
              <span style={S.tenantLabel}>TENANT</span>
              <span style={S.tenantName}>
                {tenant
                  ? (feed.agentById(tenant)?.label || feed.agentById(tenant)?.name || tenant)
                  : 'none selected — open a receptionist from the rail'}
              </span>
            </div>
          )}

          {feed.err && (
            // TRANSPORT/AUTH ONLY, and it never blanks the page: the structure is
            // declared, so the pages still render and only their DATA is missing.
            <div style={S.failed}>panel data unavailable — {feed.err}</div>
          )}

          {root.key === 'osiris' ? (
            /* OSIRIS RENDERS FULL-BLEED — it is an APP the owner opens, not a
               dashboard of readings, so it takes the content area rather than
               laying panels into the two-up grid. The other root with a bespoke
               branch is 'overview', immediately below; those two are the only
               `root.key ===` literals in this component besides the tenant bar. */
            <React.Suspense fallback={
              /* THE HONEST LOADING STATE. It names what is being fetched and why
                 it is large, because a blank panel for two seconds on a slow link
                 reads as a broken page — the same fault as a caption standing in
                 for a map. */
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flex: '1 1 auto', minHeight: 420,
                border: `1px solid ${MC.panelEdgeSoft}`, borderRadius: 16,
                background: MC.bg, color: MC.textDim, fontSize: 12,
                letterSpacing: '.08em',
              }}>
                loading the globe engine — fetched once, on first open
              </div>
            }>
              {/* OSIRIS READS THE MARKETING PROJECTION, NOT ITS OWN ROOT.
                  `board.osiris` does not exist and nothing writes it — the pins
                  are board.marketing.maps_discovery.pins, the same value the
                  Marketing map renders. Read straight off the board the surface
                  already fetched; this root issues no request of its own and
                  has no writer. */}
              <OsirisApp
                pins={((((feed.board['marketing'] || {}) as Record<string, unknown>)
                  ['maps_discovery'] || {}) as Record<string, unknown>)['pins']}
              />
            </React.Suspense>
          ) : root.key === 'overview' ? (
            <div style={S.stack}>
              <OverviewBanner domain={feed.domainLabel} roots={SURFACE.length}
                              agents={feed.roster.length} ready={feed.ready} />
              <KpiRow board={feed.board} />
              <div style={S.pair}>
                <ActivityPulse domain={domain} index={activity.index} err={activity.err} />
                <BudgetRollup board={feed.board} />
              </div>
              <div style={S.pair}>
                {/* THE HEALTH RIDES THE BOARD THE PANEL IS ALREADY POLLING —
                    no request of its own, per row or otherwise. */}
                <AgentRoster roster={feed.roster} ready={feed.ready}
                             health={((feed.board[PANEL_ROOT.ceo] || {}) as any).ceo_health} />
                <CeoReportPreview board={feed.board} />
              </div>
              <div style={S.pair}>
                <TelcoRollup board={feed.board} />
                <ApiChangeWatch board={feed.board} />
              </div>
            </div>
          ) : (
            <div style={S.stack}>
              {/* ══ THE STATUS BANNER (owner ruling 2026-09-02) ═══════════════
                  It was a fixed line of copy that said the same thing whether
                  the agent was healthy or dead — Alpha had been red on the rail
                  since 10:10 and this strip, directly above the panels, never
                  said why.

                  ⭐⭐⭐ THE THIRD SURFACE, ON THE ONE SOURCE. The rail dot, the
                  Overview roster dot and now this banner all resolve the SAME
                  `ceo_health` block through the SAME `ceoDotState`, off the
                  SAME board poll. None of them holds a verdict rule of its own,
                  so they cannot disagree about one agent.

                  WHICH ROOT IS STATEFUL IS A DECLARATION, never a name test:
                  `ROLE_ROOT.domain_ceo` is the root the harness CEO's declared
                  role points at, the same table the rail sorts controllers by.
                  A second domain-CEO root arrives here with no edit. */}
              {root.note && <RootNoteBanner note={root.note} health={ceoHealth}
                                            stateful={root.key === ROLE_ROOT.domain_ceo} />}
              {/* DELTA'S CONTROLS, ON THE ROOT HIS DECLARED ROLE POINTS AT. The run
                  control is on every Sales tab so a run can be watched from the
                  tab it fills; the list is on the workbook tab, where a company is
                  picked. The queue columns are withheld: nothing receives a queue. */}
              {root.key === ROLE_ROOT.sales
                && <DeltaRunControl tab={activeSub ? DELTA_TAB_LIST[activeSub.key] : undefined} />}
              {/* NO SCRIPT PANEL HERE. `cold_call_script` is a DECLARED panel on
                  this tab and it is the one that fills — a second surface for one
                  thing is the fault removed everywhere else in this build. The
                  producer composes it (`delta_board.script_slots`) and
                  `panels.tsx` renders it. */}
              {root.key === ROLE_ROOT.sales && activeSub && activeSub.key === DELTA_SELECTOR_SUB
                && <CompanySelector queueable={false} />}
              {/* THE PARKED COMPANIES WAITING ON THE OWNER (owner ruling 2026-09-29) — Sales'
                  workbook tab only (a park is Delta's); Bring back is the existing unpark. */}
              {root.key === ROLE_ROOT.sales && activeSub && activeSub.key === DELTA_SELECTOR_SUB
                && <ParkedPanel slot={boardRoot['parked_companies']} refresh={feed.refresh} />}
              {/* THE CALL GATE — three questions, for the row pressed in today's
                  list (`CallGateThree`). Handed the board's `refresh` so an answer
                  moves the list, the outcomes and the count at once. */}
              {root.key === ROLE_ROOT.sales && activeSub && activeSub.key === DELTA_CALLS_SUB
                && <CallGateThree refresh={feed.refresh} />}
              {/* WHAT A BOOKING TRIGGERS (owner ruling 2026-09-29) — every booked
                  consultation, its date and whether its deck is ready; Build is the
                  owner's press, Approve moves a draft to approved. The rows are the
                  producer's `consultation_bookings` slot; a build's own status is
                  polled by the panel while it runs. */}
              {root.key === ROLE_ROOT.sales && activeSub && activeSub.key === DELTA_PITCH_SUB
                && <BookingsPanel slot={boardRoot['consultation_bookings']} refresh={feed.refresh} />}
              {/* The design lays panels two-up unless one asks for the full width.
                  Consecutive half-width panels pair; a full-width one breaks the row. */}
              {groupBySpan(activeView ? activeView.panels
                           : activeSub ? activeSub.panels : root.panels).map((group, gi) =>
                group.length === 1 ? (
                  <DeclaredPanel key={group[0].key} spec={group[0]}
                         data={boardRoot[group[0].key]} tenant={tenantLabel} />
                ) : (
                  <div key={gi} style={S.pair}>
                    {group.map((spec) => (
                      <DeclaredPanel key={spec.key} spec={spec}
                                     data={boardRoot[spec.key]} tenant={tenantLabel} />
                    ))}
                  </div>
                ))}
              {/* THE LEDGER SITS AT ROOT LEVEL BECAUSE THAT IS WHERE THE NUMBER
                  IS TRUE. The design's count is per root; how those panels divide
                  across its sub-tabs is not known without the source, so putting
                  the root's total on one sub-tab would make the other six read as
                  finished. Shown on every sub-tab of the root, stated as the root's
                  total — a count that is honest from whichever tab you are on. */}
              <AwaitingPanels
                count={(activeSub ? activeSub.panels.length === 0 : root.panels.length === 0)
                       ? (root.awaiting || 0) : 0}
                where={root.label}
                subTabs={root.subTabs.length}
              />
            </div>
          )}
        </div>
      </div>

      <PulseTicker domain={domain} index={activity.index} err={activity.err} />
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  root: {
    flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
    fontFamily: MC_FONT, background: MC_BODY_BG, color: MC.text, gap: 14, padding: '4px 2px 0 2px',
  },
  brandBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 },
  brandLeft: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 },
  brandText: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  brandName: { fontSize: 14, fontWeight: 650, letterSpacing: 0.2, color: MC.text },
  brandSub: { fontSize: 11, color: MC.textMuted, letterSpacing: 0.2, whiteSpace: 'nowrap',
              overflow: 'hidden', textOverflow: 'ellipsis' },
  dotSep: { color: MC.textFaint, padding: '0 2px' },
  clockWrap: {
    display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
    background: MC.panel, border: `1px solid ${MC.panelEdge}`, borderRadius: 999,
    padding: '7px 14px', boxShadow: `inset 0 1px 0 ${MC.topHighlight}`,
  },
  clockLabel: { fontSize: 9, letterSpacing: 1.6, color: MC.textDim, fontWeight: 600 },
  clock: { fontSize: 12, letterSpacing: 1, color: MC.teal, fontVariantNumeric: 'tabular-nums', fontWeight: 600 },

  titleBlock: { display: 'flex', flexDirection: 'column', gap: 3 },
  title: { fontSize: 28, fontWeight: 700, letterSpacing: -0.4, color: MC.text, margin: 0, lineHeight: 1.1 },
  subtitle: { fontSize: 12, color: MC.textMuted, letterSpacing: 0.1, maxWidth: 900 },

  tabs: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  tab: {
    fontFamily: MC_FONT, fontSize: 11.5, letterSpacing: 0.2, padding: '8px 16px',
    border: '1px solid', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
    transition: 'color 180ms, background 180ms, border-color 180ms, box-shadow 180ms',
  },

  body: { flex: 1, minHeight: 0, display: 'flex', gap: 16 },
  rail: {
    width: 310, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12,
    background: MC_RAIL_BG, borderRadius: MC.radius, padding: '22px 18px 26px',
    border: `1px solid ${MC.line}`, boxShadow: '10px 0 48px rgba(0,0,0,.35)',
  },
  railScroll: { flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 },
  railGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  railHead: { fontSize: 8.5, letterSpacing: 1.9, color: MC.textDim, fontWeight: 700, padding: '2px' },
  railCard: {
    display: 'flex', alignItems: 'flex-start', gap: 9, textAlign: 'left',
    border: '1px solid', borderRadius: 14, padding: '9px 10px', fontFamily: MC_FONT,
    flexWrap: 'wrap',
    transition: 'background 180ms, border-color 180ms, box-shadow 180ms',
  },
  railMain: {
    display: 'flex', alignItems: 'flex-start', gap: 9, textAlign: 'left', cursor: 'pointer',
    background: 'transparent', border: 'none', padding: 0, flex: 1, minWidth: 0,
    fontFamily: MC_FONT, color: 'inherit',
  },
  // THE RUN CONTROL'S OWN LINE. `railCard` wraps, so a full-width child drops
  // beneath the name block instead of squeezing it — the control must never
  // shorten the agent's name to fit, and the strip is a sentence, not a chip.
  // The 35px inset lines it up under the text, past the badge, the way the ↳
  // slot already does.
  railRunControl: {
    width: '100%', padding: '6px 0 0 35px',
  },
  attachBtn: {
    display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', width: '100%',
    background: 'transparent', border: 'none', padding: '4px 0 0 35px', fontFamily: MC_FONT,
    textAlign: 'left',
  },
  badge: {
    flexShrink: 0, width: 34, height: 34, borderRadius: 11, border: '1px solid',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
  },
  railBody: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, flex: 1 },
  railName: { fontSize: 13, fontWeight: 700, letterSpacing: 0.1, lineHeight: 1.3 },
  railDesc: { fontSize: 11, color: MC.muted, letterSpacing: 0.1, lineHeight: 1.35 },
  attach: { display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 },
  attachArrow: { fontSize: 11, color: MC.attach },
  attachName: { fontSize: 11, color: MC.attach, letterSpacing: 0.3, fontWeight: 600 },
  attachPending: { fontSize: 11, color: MC.attach, letterSpacing: 0.1, fontStyle: 'italic' },
  // ⭐ `dot` IS THE BRAND BAR'S LIVE-PULSE DOT AND NOTHING ELSE NOW. It briefly
  // held only `marginTop` while the rail's box moved into `RootStateDot`, and
  // that silently shrank the header dot to 0x0 — one style object serving two
  // dots, so emptying it for one emptied it for both. jsdom saw nothing.
  dot: { flexShrink: 0, width: 8, height: 8, borderRadius: 999, marginTop: 9 },
  // The rail slot is LAYOUT ONLY: the root dot's box belongs to its component.
  rootDotSlot: { marginTop: 9 },
  railPending: { fontSize: 9, lineHeight: 1.45, color: MC.textDim, padding: '2px 4px 5px 4px' },

  miniRow: { display: 'flex', gap: 8, flexShrink: 0 },
  miniTile: {
    flex: 1, background: MC.panel, border: `1px solid ${MC.panelEdgeSoft}`, borderRadius: 14,
    padding: '10px 11px', display: 'flex', flexDirection: 'column', gap: 2,
    boxShadow: `inset 0 1px 0 ${MC.topHighlight}`,
  },
  miniValue: { fontSize: 19, fontWeight: 700, color: MC.teal, lineHeight: 1, fontVariantNumeric: 'tabular-nums' },
  miniLabel: { fontSize: 7.5, letterSpacing: 1.2, color: MC.textDim, fontWeight: 600 },

  well: { flex: 1, minWidth: 0, overflowY: 'auto', paddingRight: 4, display: 'flex', flexDirection: 'column', gap: 14 },
  subTabs: {
    display: 'flex', flexWrap: 'wrap', gap: 2, borderBottom: `1px solid ${MC.panelEdgeSoft}`, flexShrink: 0,
  },
  subTab: {
    fontFamily: MC_FONT, fontSize: 11, letterSpacing: 0.2, padding: '9px 14px',
    background: 'transparent', border: 'none', borderBottom: '2px solid transparent',
    cursor: 'pointer', whiteSpace: 'nowrap', transition: 'color 180ms, border-color 180ms',
  },
  tenantBar: {
    display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
    background: MC.panel, border: `1px solid ${MC.panelEdgeSoft}`, borderRadius: 999,
    padding: '7px 14px', alignSelf: 'flex-start',
  },
  tenantLabel: { fontSize: 8, letterSpacing: 1.5, color: MC.textDim, fontWeight: 700 },
  tenantName: { fontSize: 11, color: MC.violet, fontWeight: 600 },

  agentBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
    flexShrink: 0, background: MC.panel, border: `1px solid ${MC.panelEdge}`,
    borderRadius: MC.radius, padding: '10px 14px',
    boxShadow: `inset 0 1px 0 ${MC.topHighlight}`,
  },
  agentWho: { display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 },
  agentName: { fontSize: 12.5, fontWeight: 700, letterSpacing: 0.8, color: MC.text },
  agentRole: { fontSize: 10, color: MC.textDim, letterSpacing: 0.2 },
  agentTabs: { display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 },
  agentTab: {
    fontFamily: MC_FONT, fontSize: 9.5, letterSpacing: 1.4, padding: '7px 15px',
    borderRadius: 999, border: '1px solid transparent', whiteSpace: 'nowrap',
  },
  viewTabs: { display: 'flex', flexWrap: 'wrap', gap: 7, flexShrink: 0 },
  viewTab: {
    fontFamily: MC_FONT, fontSize: 10.5, letterSpacing: 0.4, padding: '7px 14px',
    border: '1px solid', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
    transition: 'color 180ms, background 180ms, border-color 180ms',
  },
  stack: { display: 'flex', flexDirection: 'column', gap: 14 },
  pair: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 14, alignItems: 'start' },
  failed: { fontSize: 11, color: MC.red, padding: '2px 0', flexShrink: 0 },
};
